import { differenceInCalendarDays, format, isThisYear, isToday, isTomorrow, isYesterday, startOfDay } from 'date-fns';

/** ClickUp-style short due date: Today / Tomorrow / Yesterday / Fri / 10/31/26 */
export function formatDue(ts: number | null | undefined, withTime = false): string {
  if (ts === null || ts === undefined) return '';
  const diff = differenceInCalendarDays(ts, new Date());
  let label: string;
  if (diff === 0) label = 'Today';
  else if (diff === 1) label = 'Tomorrow';
  else if (diff === -1) label = 'Yesterday';
  else if (diff > 1 && diff < 7) label = format(ts, 'EEE');
  else label = format(ts, 'M/d/yy');
  if (withTime) label += ` ${format(ts, 'h:mm a')}`;
  return label;
}

export type DueTone = 'overdue' | 'today' | 'soon' | 'normal' | 'done';

export function dueTone(ts: number | null | undefined, closed = false): DueTone {
  if (ts === null || ts === undefined) return 'normal';
  if (closed) return 'done';
  const diff = differenceInCalendarDays(ts, new Date());
  if (diff < 0) return 'overdue';
  if (diff === 0) return 'today';
  if (diff <= 2) return 'soon';
  return 'normal';
}

export function formatDateShort(ts: number | null | undefined): string {
  if (!ts) return '';
  return format(ts, 'M/d/yy');
}

export function formatDateLong(ts: number | null | undefined): string {
  if (!ts) return '';
  return format(ts, isThisYear(ts) ? 'MMM d' : 'MMM d, yyyy');
}

/** Activity / inbox timestamps: 12:31 PM today, Yesterday at 1:41 PM, Oct 19 */
export function formatWhen(ts: number): string {
  if (isToday(ts)) return format(ts, 'h:mm a');
  if (isYesterday(ts)) return `Yesterday at ${format(ts, 'h:mm a')}`;
  return format(ts, isThisYear(ts) ? 'MMM d' : 'MMM d, yyyy');
}

export function formatWhenLong(ts: number): string {
  if (isToday(ts)) return `Today at ${format(ts, 'h:mm a')}`;
  if (isYesterday(ts)) return `Yesterday at ${format(ts, 'h:mm a')}`;
  return format(ts, isThisYear(ts) ? "MMM d 'at' h:mm a" : "MMM d, yyyy 'at' h:mm a");
}

export function relativeTime(ts: number): string {
  const s = Math.round((Date.now() - ts) / 1000);
  if (s < 45) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min${m === 1 ? '' : 's'} ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d} day${d === 1 ? '' : 's'} ago`;
  return formatDateLong(ts);
}

export function formatDuration(ms: number | null | undefined, opts: { seconds?: boolean } = {}): string {
  if (!ms || ms <= 0) return opts.seconds ? '0:00:00' : '';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (opts.seconds) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${Math.max(1, m)}m`;
}

/** Parses "3h 20m", "1.5h", "90m", "2" (hours) into milliseconds. */
export function parseDuration(input: string): number | null {
  const s = input.trim().toLowerCase();
  if (!s) return null;
  let total = 0;
  let matched = false;
  for (const m of s.matchAll(/(\d+(?:\.\d+)?)\s*(w|d|h|m|s)?/g)) {
    matched = true;
    const n = parseFloat(m[1]);
    const unit = m[2] ?? 'h';
    total += n * { w: 5 * 8 * 3600e3, d: 8 * 3600e3, h: 3600e3, m: 60e3, s: 1e3 }[unit as 'h'];
  }
  return matched && total > 0 ? Math.round(total) : null;
}

export function formatMoney(v: number, currency = 'USD', precision = 2) {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: precision, minimumFractionDigits: precision }).format(v);
  } catch {
    return `${v.toFixed(precision)} ${currency}`;
  }
}

export function formatBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(1)} GB`;
}

export function dayStart(ts: number) {
  return startOfDay(ts).getTime();
}

export function greeting(d = new Date()) {
  const h = d.getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

/** Someone's custom status (Set status in the account menu), or '' once it has cleared itself. */
export function userStatus(user: { status?: string | null; statusUntil?: number | null } | undefined, now = Date.now()): string {
  if (!user?.status) return '';
  return user.statusUntil && user.statusUntil <= now ? '' : user.status;
}

/** When a status clears itself, as "Until 8:24 PM", "Until tomorrow" (it ends tonight) or "Until Monday". */
export function statusUntilLabel(ts: number, now = Date.now()): string {
  const d = new Date(ts);
  if (d.getHours() === 23 && d.getMinutes() === 59) {
    // clears at midnight: name the day it's back
    const back = ts + 60_000;
    if (isTomorrow(back)) return 'Until tomorrow';
    return `Until ${format(back, differenceInCalendarDays(back, now) < 7 ? 'EEEE' : 'MMM d')}`;
  }
  if (isToday(ts)) return `Until ${format(ts, 'h:mm a')}`;
  if (isTomorrow(ts)) return `Until tomorrow ${format(ts, 'h:mm a')}`;
  return `Until ${format(ts, 'EEE h:mm a')}`;
}
